import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  BookOpen,
  ChevronRight,
  Link2,
  Lock,
  Monitor,
  Moon,
  ShieldCheck,
  Sun,
  UserRound,
} from "lucide-react";
import {
  AsyncSection,
  Button,
  NativeSelect,
  SectionHeader,
  SegmentedControl,
  SettingRow,
  SettingsGroup,
  SettingsDetailsButton,
  SettingsMetaCard,
  SettingsPageLayout,
  Skeleton,
  StatusDot,
  Switch,
  avatarToneFor,
  useMediaQuery,
  useSettingsAsideBeside,
} from "@playbookai/ui";
import { DEFAULT_PLATFORM_THEME, PRODUCT_NAME } from "@playbookai/types";
import { startPasswordChange } from "../../api/password-change-api.js";
import { useAuthStore } from "../../auth/auth-store.js";
import { canAdministerWorkspace } from "../../auth/roles.js";
import { getDocumentationUrl } from "../../config/runtime.js";
import { getEnabledModules } from "../../modules/index.js";
import {
  MODULE_DOT_CLASS,
  moduleDotProps,
} from "../../modules/launcher-entries.js";
import { ADMIN_ROUTE, SETTINGS_ROUTE } from "../../routes.js";
import { useDateTimeFormatter } from "../../settings/format-date-time.js";
import { timeZoneGroups, timeZoneLabel } from "../../settings/time-zones.js";
import type { PresentationTheme } from "../../settings/types.js";
import { usePresentationTheme } from "../../settings/usePresentationTheme.js";
import { usePresentationTimeZone } from "../../settings/usePresentationTimeZone.js";
import { usePresentationPreferencesQuery } from "../../settings/useSettings.js";

/* Account — the Settings index, built to the Direction B artboard (PB-5134).
 *
 * Identity comes from the validated session claims, never from a form: the
 * IdP owns name and email. Appearance and Region are INSTANT single-field
 * writes on the platform presentation preference (d27/d29), the same row the
 * TopBar toggle drives, so both controls always agree on value and version;
 * there is no Save bar here. The page-level states follow the artboard: a
 * skeleton while that preference row is first read, and the error box with
 * Retry when the read failed (identity from the session never fails to load). */

/* The artboard's Appearance group: a Match device | Light | Dark choice and a
 * separate High contrast switch. The preference stores ONE of system | light |
 * dark | high-contrast (d17; light path d24), and the design system's
 * high-contrast palette is dark-based, so the switch maps to the
 * "high-contrast" value and the choice shows Dark while it is on; picking a
 * choice switches it off again, and so does the switch itself, back to Match
 * device. */
type ThemeBase = "system" | "light" | "dark";

const THEME_BASE_OPTIONS: readonly {
  value: ThemeBase;
  label: string;
  icon: ReactElement;
}[] = [
  {
    value: "system",
    label: "Match device",
    icon: <Monitor className="h-3.5 w-3.5" />,
  },
  { value: "light", label: "Light", icon: <Sun className="h-3.5 w-3.5" /> },
  { value: "dark", label: "Dark", icon: <Moon className="h-3.5 w-3.5" /> },
];

function themeBase(theme: PresentationTheme): ThemeBase {
  return theme === "system" || theme === "light" ? theme : "dark";
}

/* A note under a settings row, in the row description's type. */
const NOTE = "mt-0.5 text-body-small text-pb-text-muted";

/* Kept hand-built, both chips: Badge pill paints text-pb-text on its own
   leading and px-2 (CHIP), and Badge lg fills bg-pb-card-elevated
   (MODULE_CHIP); no PR 3 look row approves either. */
const CHIP =
  "inline-flex items-center gap-1.5 rounded-full border border-pb-border bg-pb-card-elevated px-2.5 py-0.5 text-caption font-medium text-pb-text-secondary";
const MODULE_CHIP =
  "inline-flex items-center gap-2 rounded-md border border-pb-border bg-pb-card px-3 py-1.5 text-body-small font-medium text-pb-text";
function initialsOf(name: string | null, email: string | null): string {
  const source = name?.trim() || email?.split("@")[0] || "";
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  const letters =
    parts.length > 1
      ? `${parts[0]?.[0] ?? ""}${parts[parts.length - 1]?.[0] ?? ""}`
      : source.slice(0, 2);
  return letters.toUpperCase() || "?";
}

function AccountSkeleton(): ReactElement {
  /* The same call `SettingsPageLayout` makes (PB-5216 d2; rail-aware since
     PB-5436): a skeleton that split on its own rule drew two columns where the
     loaded page drew one, so the content jumped sideways the moment it
     arrived. A media query alone also kept the side panel beside a page the
     Player AI rail had squeezed. */
  const asideBeside = useSettingsAsideBeside();
  return (
    <div
      className={
        asideBeside ? "flex flex-row items-start gap-6" : "flex flex-col gap-6"
      }
    >
      <div className="flex min-w-0 flex-1 flex-col gap-3.5">
        <Skeleton shape="card" className="h-24" />
        <Skeleton shape="panel" className="h-11" />
        <Skeleton className="mt-3 h-16" />
        <Skeleton className="h-16" />
        <Skeleton className="h-16" />
        <Skeleton className="h-16" />
      </div>
      <Skeleton
        shape="panel"
        className={asideBeside ? "h-36 w-74 shrink-0" : "h-36 shrink-0"}
      />
    </div>
  );
}

/** The one genuinely live value on the page: a clock that follows the saved
 * time zone. It owns its own ticking state and lives in its own component so
 * only this line re-renders each second — not the whole settings page. */
function CurrentTimeInZone(): ReactElement {
  const formatter = useDateTimeFormatter();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <time dateTime={new Date(now).toISOString()}>
      {formatter.dateTime(now)}
    </time>
  );
}

/* The Password section (PB-5415), for local accounts only.
 *
 * It holds no password and no form. Selecting the action asks the BFF for a
 * Keycloak URL and navigates there; Keycloak collects the credential behind a
 * fresh challenge and enforces the realm policy. The server re-derives
 * eligibility for itself, so this component deciding to render is a
 * presentation choice, never the gate. */
function PasswordSection(): ReactElement {
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState(false);

  async function begin(): Promise<void> {
    setStarting(true);
    setFailed(false);
    try {
      /* Where a CANCELLED action returns to. A completed one never comes back
         here — every session ends and the server sends the person to sign in. */
      const url = await startPasswordChange(SETTINGS_ROUTE);
      window.location.assign(url);
    } catch {
      /* The message deliberately carries nothing from the server. A refusal
         here is the same for a suspended member and a federated one, and
         repeating a status would start to tell them apart. */
      setStarting(false);
      setFailed(true);
    }
  }

  return (
    <SettingsGroup variant="settings" title="Password">
      {/* The description says what actually happens. Revocation is
          identity-wide — every workspace this person belongs to, plus the
          shared sign-in — so this promise is now the whole of it (PB-5415). */}
      <SettingRow
        label="Change password"
        description={
          "After your password changes, you\u2019ll be signed out on every " +
          "device and will need to sign in again with your new password."
        }
        control={
          <Button
            type="button"
            variant="secondary"
            disabled={starting}
            aria-describedby="account-password-status"
            onClick={() => void begin()}
          >
            {starting ? "Opening\u2026" : "Change password"}
          </Button>
        }
      />
      {/* Always present, so assistive technology announces the transition
          rather than discovering a new region. Empty until there is news. */}
      <p id="account-password-status" role="status" className={NOTE}>
        {starting
          ? "Taking you to the sign-in service to change your password\u2026"
          : failed
            ? "Couldn't start a password change. Try again."
            : ""}
      </p>
    </SettingsGroup>
  );
}

export function AccountSettings(): ReactElement {
  const navigate = useNavigate();
  const claims = useAuthStore((state) => state.claims);
  const isAdmin = canAdministerWorkspace(claims);
  const modules = getEnabledModules(claims?.enabledModules);
  const documentationUrl = getDocumentationUrl();
  const preferences = usePresentationPreferencesQuery();
  const { theme, status: themeStatus, setTheme } = usePresentationTheme();
  const region = usePresentationTimeZone();
  // Match device turns this device request into high contrast; an explicit
  // Light or Dark pick is not overridden. Saying so keeps "High contrast"
  // honest: under Match device the switch is not what turned it on.
  const osRequestsContrast = useMediaQuery("(prefers-contrast: more)");
  const zoneGroups = useMemo(
    () => timeZoneGroups([region.timeZone]),
    [region.timeZone],
  );

  const meta =
    isAdmin || documentationUrl ? (
      <div className="flex flex-col gap-3.5">
        {isAdmin ? (
          <SettingsMetaCard
            icon={ShieldCheck}
            title="Admin"
            description="Members, roles and billing are managed in the Admin console."
            action={
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => navigate(ADMIN_ROUTE)}
              >
                Open Admin console
                <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              </Button>
            }
          />
        ) : null}
        {documentationUrl ? (
          // Only links with a real destination (d30): the artboard's keyboard
          // shortcuts and what's-new rows return when those surfaces exist.
          <SettingsMetaCard
            icon={Link2}
            title="Quick links"
            links={[
              {
                label: "Documentation",
                icon: BookOpen,
                href: documentationUrl,
                external: true,
                accessory: <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />,
              },
            ]}
          />
        ) : null}
      </div>
    ) : undefined;

  const header = (
    <SectionHeader
      variant="settings"
      leading={<UserRound className="w-4 h-4 text-pb-cyan-text" />}
      title="Account"
      description="Your identity, sign-in and personal preferences."
      actionsSlot={<SettingsDetailsButton meta={meta} />}
    />
  );

  if (!preferences.data) {
    /* The wait and the failed first read through AsyncSection: the same
       skeleton, and the same error panel with its Retry. */
    return (
      <div>
        {header}
        <AsyncSection
          status={preferences.isError ? "error" : "loading"}
          loadingLabel="your account"
          errorTitle="Couldn't load your account"
          errorDescription="Your profile is temporarily unavailable. Reload the page to try again."
          onRetry={() => void preferences.refetch()}
          skeleton={<AccountSkeleton />}
        >
          {null}
        </AsyncSection>
      </div>
    );
  }

  const displayName = claims?.name?.trim() || claims?.email || "Signed-in user";
  /* Sign-in method owns the Entra notice independently of rollout state. The
     password button additionally requires the deployment gate, which fails
     closed for an older BFF. */
  const signsInWithPassword =
    claims?.authenticationMethod === "local" &&
    claims.passwordChangeSelfServiceEnabled;
  const signsInWithEntra = claims?.authenticationMethod === "microsoft-entra";
  // Only a write can be pending here: the row has loaded.
  const themeSaving = themeStatus === "pending";
  const regionHint = region.saving
    ? "Saving…"
    : region.conflict
      ? "Updated elsewhere — showing the latest saved time zone."
      : region.persisted
        ? "Saved to your account."
        : "Detected from your browser; override if needed.";

  return (
    <div className="pb-motion-content-enter">
      {header}
      <SettingsPageLayout meta={meta} mainWidth="fluid">
        {/* Kept hand-built: Card has no px-5 py-4.5 padding option (CardBody
            is px-4 py-4), a look change no PR 3 row approves. */}
        <div className="mb-3.5 flex items-center gap-4 rounded-xl border border-pb-border/80 bg-pb-card px-5 py-4.5">
          {/* Kept hand-built: Avatar's size ramp stops at lg (48px) and has
              no step for today's 56px disc. A height or width class on the
              part is not allowed, and 48px is a look change no PR 3 row
              approves (OK-65 covers the seed colour only). */}
          <span
            aria-hidden
            className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-heading-2 ${avatarToneFor(claims?.userId ?? displayName)}`}
          >
            {initialsOf(claims?.name ?? null, claims?.email ?? null)}
          </span>
          <div className="min-w-0">
            <p className="truncate text-heading-3 text-pb-text">
              {displayName}
            </p>
            <p className="truncate text-body text-pb-text-secondary">
              {claims?.email ?? "—"}
            </p>
            <ul
              aria-label="Workspace and role"
              className="mt-2 flex flex-wrap gap-2"
            >
              <li className={CHIP}>
                {/* Kept: StatusDot has no teal-text paint (teal is not a
                    tone, and the vsm module tint is another token). */}
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 rounded-full bg-pb-teal-text"
                />
                {claims?.tenantName ?? "—"}
              </li>
              <li className={CHIP}>{isAdmin ? "Admin" : "Member"}</li>
            </ul>
          </div>
        </div>
        {/* Conditional since PB-5415. This claimed Microsoft Entra owned the
            name and email of EVERY signed-in person, which was never true for a
            local account — and is the kind of wrong that sends someone to an
            administrator who cannot help. A session that cannot say which
            method it used says neither.

            Kept hand-built: Alert tone="neutral" is an 8px box with the opaque
            elevated fill, py-2.5 and its own glyph, a look change no PR 3 row
            approves. */}
        {signsInWithEntra ? (
          <div className="mb-6.5 flex items-start gap-2 rounded-lg border border-pb-border/70 bg-pb-card-elevated/40 px-3 py-3 text-caption text-pb-text-secondary">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              Your name and email come from your organization&rsquo;s single
              sign-on (Microsoft Entra). Contact your admin to change them.
            </span>
          </div>
        ) : null}

        {/* Between the identity card and Your modules, per the ticket's
            layout. Rendered ONLY on an explicit `local`: absent means unknown,
            and offering a password change to a federated member would be the
            wrong way to be wrong. */}
        {signsInWithPassword ? <PasswordSection /> : null}

        <SettingsGroup
          variant="settings"
          title="Your modules"
          description={`The ${PRODUCT_NAME} modules enabled for you in this workspace.`}
        >
          {modules.length ? (
            <ul
              aria-label="Enabled modules"
              className="mt-3 flex flex-wrap gap-2"
            >
              {modules.map((module) => (
                <li key={module.id} className={MODULE_CHIP}>
                  <StatusDot
                    size="sm"
                    data-tone={MODULE_DOT_CLASS[module.id]}
                    {...(moduleDotProps(module.id) ?? { tone: "neutral" })}
                  />
                  {module.label}
                </li>
              ))}
            </ul>
          ) : (
            /* Kept as today's 14px line: EmptyState size="sm" sets 11px
               type, a look change no PR 3 row approves. */
            <p className="mt-3 text-body text-pb-text-muted">
              No modules are enabled for you yet.
            </p>
          )}
        </SettingsGroup>

        <SettingsGroup variant="settings" title="Appearance">
          <SettingRow
            label={<span id="account-theme-label">Theme</span>}
            description={`Match light or dark across ${PRODUCT_NAME}.`}
            control={
              <SegmentedControl<ThemeBase>
                aria-labelledby="account-theme-label"
                className="max-w-full flex-wrap self-start sm:shrink-0"
                value={themeBase(theme)}
                onChange={setTheme}
                options={THEME_BASE_OPTIONS.map((option) => ({
                  ...option,
                  disabled: themeSaving,
                }))}
              />
            }
          />
          <SettingRow
            label={<span id="account-high-contrast-label">High contrast</span>}
            description="Increase contrast for readability."
            control={
              <Switch
                aria-labelledby="account-high-contrast-label"
                checked={theme === "high-contrast"}
                disabled={themeSaving}
                onChange={(on) =>
                  setTheme(on ? "high-contrast" : DEFAULT_PLATFORM_THEME)
                }
              />
            }
          />
          {/* Kept as today's 13px line: SaveStatus sets 11-12px type, a look
              change no PR 3 row approves. */}
          {themeSaving ? (
            <p role="status" className={NOTE}>
              Saving…
            </p>
          ) : null}
          {osRequestsContrast ? (
            <p className={NOTE}>
              Your device asks for more contrast, so Match device shows{" "}
              {PRODUCT_NAME} in high contrast. A Light or Dark pick is not
              overridden.
            </p>
          ) : null}
        </SettingsGroup>

        <SettingsGroup
          variant="settings"
          title="Region"
          description={`Used for dates and times shown in ${PRODUCT_NAME}.`}
        >
          <SettingRow
            label={<label htmlFor="account-time-zone">Time zone</label>}
            description={
              <span id="account-time-zone-hint" role="status">
                {regionHint}
              </span>
            }
            control={
              <div className="sm:w-auto sm:min-w-48">
                <NativeSelect
                  id="account-time-zone"
                  value={region.timeZone}
                  disabled={region.saving}
                  aria-describedby="account-time-zone-hint"
                  onChange={region.setTimeZone}
                  groups={zoneGroups.map((group) => ({
                    label: group.label,
                    options: group.zones.map((zone) => ({
                      value: zone,
                      label: timeZoneLabel(zone),
                    })),
                  }))}
                />
              </div>
            }
          />
          <p className={NOTE}>
            Current time in this zone: <CurrentTimeInZone />
          </p>
        </SettingsGroup>
      </SettingsPageLayout>
    </div>
  );
}
